/**
 * 需要想定 daily ページデータ取得 hook。
 *
 * GetDemandForecastDaily RPC を呼んで 1 response で全小売 draft + BG 全体 + 比較系列を取得する。
 * 対象/小売/エリアの view 切替はローカルで行い、再 fetch しない。
 */
import { useCallback, useRef } from 'react'

import { create } from '@bufbuild/protobuf'
import type { MessageShape } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { getDemandForecastDaily } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import {
  GetDemandForecastDailyRequestSchema,
  GetDemandForecastDailyResponseSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'

import { WEATHER_REFETCH_INTERVAL_MS } from '~/hooks/weather/refetchInterval'

type Params = {
  caseId: string | undefined
  areaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  /**
   * 過去実績取得対象日リスト (YYYY-MM-DD 文字列 → ProtoDate に変換して送る)。
   * 類似日ダイアログ「適用」で選んだ日付群を渡す。省略時は backend 既定。
   */
  pastActualDates?: ProtoDate[]
  /**
   * 気象観測地点の画面内一時切替 (weather-point-in-screen-temp-switch design
   * D1)。指定時、backend は気象の「表示」解決のみをこの地点へ切り替える
   * (需要想定値・類似日・確定には非連動)。undefined = 組織共通設定のまま。
   */
  locationName?: string
  /**
   * 画面が read-only (需要想定確定 / WF 完了 / 削除済み) か。 **`baseKey` に含める**。
   *
   * 確定すると [[useWeatherPointOverride]] が override を落として `locationName` が
   * undefined に戻るが、 read-only 状態を `baseKey` に入れていないと「同じ baseKey」
   * と判定され、 **直前の一時地点の response が placeholder として描かれる**。
   * 確定後は一時切替 UI と「(一時表示)」 表記が hide される (D6) ため、 利用者からは
   * 組織共通設定の気象に見えてしまう。 read-only の反転を別 key として扱い、
   * その瞬間は placeholder を使わせない。
   */
  isReadonly?: boolean
}

/**
 * 需要想定 daily ページを取得する。
 * caseId / areaCode / targetDate のいずれかが未定義の場合は fetch を無効化する。
 */
export function useGetDemandForecastDaily({
  caseId,
  areaCode,
  section,
  targetDate,
  pastActualDates = [],
  locationName,
  isReadonly = false,
}: Params): UseQueryResult<
  MessageShape<typeof GetDemandForecastDailyResponseSchema>,
  ConnectError
> {
  const enabled =
    caseId !== undefined &&
    caseId !== '' &&
    areaCode !== undefined &&
    targetDate !== undefined

  /**
   * **`locationName` 以外**の入力の同一性キー。
   *
   * 気象観測地点の一時切替 (AC-DFC-008a) は「表示だけの一時利用」 なのに、
   * `locationName` が page 全体クエリの queryKey に入っているため、 地点を変えると
   * `data` が一旦 undefined に戻り、 48 コマテーブルと需要チャートが**消えて再描画**
   * されていた (#7454)。 地点を見比べながら想定を作る操作は切替を何度も行うので、
   * そのたびにちらつきと待ちが入る。
   */
  const baseKey = JSON.stringify({
    caseId,
    areaCode,
    section,
    targetDate,
    pastActualDates,
    // 確定の前後をまたいで placeholder を持ち越さない (上の `isReadonly` 参照)。
    isReadonly,
  })

  /**
   * 直近に取得できた「同じ `baseKey` の」 response。 placeholder はここからだけ出す。
   *
   * **素の `placeholderData: keepPreviousData` には戻せない。** 以前それを使っていたが、
   * 断面切替・過去実績日変更・エリア切替・JST-midnight refetch にも効いてしまい、
   * tomorrow→today の切替で**旧断面の 48 コマ値・気象・gcKoma が一瞬描画される**
   * 退行が出たため削除された (PR #6000 review)。 `baseKey` を突き合わせることで、
   * 「地点だけが変わった時」 に対象を絞る。
   */
  const lastByBaseKey = useRef<
    | {
        baseKey: string
        data: MessageShape<typeof GetDemandForecastDailyResponseSchema>
      }
    | undefined
  >(undefined)

  const result = useQuery(
    getDemandForecastDaily,
    create(GetDemandForecastDailyRequestSchema, {
      caseId: caseId ?? '',
      areaCode: areaCode ?? 0,
      section,
      targetDate,
      pastActualDates,
      locationName,
      // Prevent an old bundle from silently omitting the unattributed
      // breakdown from the BG total.
      supportsUnattributed: true,
    }),
    {
      enabled,
      staleTime: 30_000,
      // AC-DFC-016 (#9296): 確定済み表示でも止めない (実績は確定後も更新される。AC-DFC-014)
      refetchInterval: WEATHER_REFETCH_INTERVAL_MS,
      // 地点だけが変わった時に限り、 直前の response を placeholder として出す
      // (= `data` が undefined にならないので `isLoading` が立たず、 48 コマ
      // テーブルと需要チャートが消えない)。 他の入力が変わった時は undefined を
      // 返し、 従来どおり読み込み表示に落とす。
      placeholderData: () =>
        lastByBaseKey.current?.baseKey === baseKey
          ? lastByBaseKey.current.data
          : undefined,
      // NOTE: 元々 `placeholderData: keepPreviousData` で override 変更時の flicker を
      // 抑えていたが、断面切替・過去実績日変更・エリア切替・JST-midnight refetch など
      // 他 key 変更にも適用され、tomorrow→today 切替時に旧断面の 48 コマ値・気象・
      // gcKoma が placeholder として一瞬描画される退行が出るため削除 (PR #6000 review)。
      // override 変更時は一瞬 spinner が出るが、正しさを優先する。
      // InvalidArgument (= ケースは取得できているが本体の前提が未充足: ケースに BG が無い、
      // D3 の地点 override 不正 等。見つからない / 形式不正な workflowId は
      // GetWorkflowCase 側で AC-WFW-040 として扱う) は
      // ページ内で専用エラー画面を出すため error boundary に投げず pageError として
      // 観測する。それ以外のエラーは global QueryClient の throwOnError: true を
      // 踏襲して ErrorLayout に委譲する (= 本 override が無いと InvalidArgument も
      // boundary に飛び、ページ内分岐が本番で dead code になる)。
      // 表示中の data がある間の再取得 (AC-DFC-016) の失敗は投げない: 通信断 1 回で
      // 画面ごと消え、 未保存の編集を失うため。
      throwOnError: (error, query) =>
        query.state.data === undefined &&
        (!(error instanceof ConnectError) ||
          (error.code !== Code.InvalidArgument &&
            error.code !== Code.FailedPrecondition)),
    },
  )

  // placeholder ではない実データだけを覚える (placeholder を覚えると、 古い response が
  // baseKey をまたいで生き延びる)。 render 中に書くのは「直前の値」 を保持する定石で、
  // 参照するのは次の render の placeholder 計算だけ。
  if (result.data !== undefined && !result.isPlaceholderData) {
    lastByBaseKey.current = { baseKey, data: result.data }
  }

  return result
}

/**
 * 保存の成功時に呼ぶ: その時点で進行中の page 取得を取り消し、 保存の後の取得として
 * 出し直す (#9296)。
 */
export function useRestartInFlightDemandForecastDailyFetches(): () => Promise<void> {
  const queryClient = useQueryClient()
  return useCallback(async (): Promise<void> => {
    const inFlight = {
      queryKey: createConnectQueryKey({
        schema: getDemandForecastDaily,
        cardinality: 'finite',
      }),
      fetchStatus: 'fetching',
    } as const
    const restarting = new Set(queryClient.getQueryCache().findAll(inFlight))
    if (restarting.size === 0) return
    // refetchQueries の cancelRefetch は data の無い取得を止めないので、 先に取り消す
    await queryClient.cancelQueries(inFlight)
    void queryClient.refetchQueries({
      queryKey: inFlight.queryKey,
      type: 'active',
      predicate: (query) => restarting.has(query),
    })
  }, [queryClient])
}
